import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  getPlaywrightArtifactUrl,
  type PlaywrightArtifact,
  type PlaywrightArtifactManifest,
} from "./playwright-api";

interface ArtifactGalleryProps {
  manifest: PlaywrightArtifactManifest | null;
  loading: boolean;
  runId?: string;
  selectedTestKeys?: string[];
}

const previewUrl = (artifact: PlaywrightArtifact) =>
  artifact.previewUrl || artifact.inlineUrl || "";

const statusLabel = (artifact: PlaywrightArtifact) =>
  artifact.labels.find((label) => label.startsWith("status:"))?.slice(7) || "";

const ArtifactGallery = ({
  manifest,
  loading,
  runId,
  selectedTestKeys = [],
}: ArtifactGalleryProps) => {
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [spec, setSpec] = useState("");
  const [test, setTest] = useState("");
  const [tag, setTag] = useState("");
  const [lightbox, setLightbox] = useState<PlaywrightArtifact | null>(null);
  const [downloadError, setDownloadError] = useState("");

  const baseArtifacts = useMemo(
    () =>
      (manifest?.artifacts ?? []).filter(
        (artifact) =>
          selectedTestKeys.length === 0 ||
          selectedTestKeys.includes(artifact.testKey)
      ),
    [manifest, selectedTestKeys]
  );
  const options = useMemo(
    () => ({
      statuses: Array.from(new Set(baseArtifacts.map(statusLabel).filter(Boolean))).sort(),
      specs: Array.from(new Set(baseArtifacts.map((artifact) => artifact.spec).filter(Boolean))).sort(),
      tests: Array.from(new Set(baseArtifacts.map((artifact) => artifact.test).filter(Boolean))).sort(),
    }),
    [baseArtifacts]
  );
  const artifacts = useMemo(() => {
    const normalizedTag = tag.trim().toLowerCase();
    return baseArtifacts.filter(
      (artifact) =>
        (!kind || artifact.kind === kind) &&
        (!status || statusLabel(artifact) === status) &&
        (!spec || artifact.spec === spec) &&
        (!test || artifact.test === test) &&
        (!normalizedTag ||
          artifact.labels.some((label) => label.toLowerCase().includes(normalizedTag)))
    );
  }, [baseArtifacts, kind, spec, status, tag, test]);

  const downloadArtifact = async (artifact: PlaywrightArtifact) => {
    if (!runId) return;
    setDownloadError("");
    try {
      const url = await getPlaywrightArtifactUrl(runId, artifact.id, { download: true });
      window.location.assign(url);
    } catch (error: any) {
      setDownloadError(error?.response?.data?.message || error?.message || "Download failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12">
        <CircularProgress size={22} />
        <Typography color="text.secondary">Loading artifacts…</Typography>
      </div>
    );
  }
  if (manifest?.artifactError) return <Alert severity="error">{manifest.artifactError}</Alert>;
  if (!manifest?.artifactsReady) {
    return <Alert severity="info">Artifacts are not ready yet.</Alert>;
  }
  if (baseArtifacts.length === 0) {
    return <Typography color="text.secondary">No artifacts were captured.</Typography>;
  }

  const filterSelect = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    values: string[],
    allLabel: string
  ) => (
    <FormControl size="small" fullWidth>
      <InputLabel id={`artifact-${label.toLowerCase().replace(" ", "-")}-label`}>
        {label}
      </InputLabel>
      <Select
        labelId={`artifact-${label.toLowerCase().replace(" ", "-")}-label`}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {values.map((option) => (
          <MenuItem key={option} value={option}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2 mb-4">
        {filterSelect(
          "Artifact kind",
          kind,
          setKind,
          ["screenshot", "video", "trace", "log"],
          "All kinds"
        )}
        {filterSelect("Status", status, setStatus, options.statuses, "All statuses")}
        {filterSelect("Spec", spec, setSpec, options.specs, "All specs")}
        {filterSelect("Test", test, setTest, options.tests, "All tests")}
        <TextField
          size="small"
          label="Artifact tag"
          value={tag}
          onChange={(event) => setTag(event.target.value)}
        />
      </div>
      {downloadError && <Alert severity="error" sx={{ mb: 2 }}>{downloadError}</Alert>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {artifacts.map((artifact) => {
          const url = previewUrl(artifact);
          if (artifact.kind === "screenshot") {
            return (
              <Paper key={artifact.id} variant="outlined" sx={{ overflow: "hidden" }}>
                <button
                  type="button"
                  className="block w-full bg-gray-100"
                  onClick={() => setLightbox(artifact)}
                  aria-label={`Open ${artifact.name}`}
                >
                  <img
                    src={url}
                    alt={artifact.name}
                    className="h-48 w-full object-contain"
                  />
                </button>
                <div className="p-3">
                  <Typography variant="body2" fontWeight={600}>{artifact.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {artifact.spec} · {artifact.test}
                  </Typography>
                </div>
              </Paper>
            );
          }
          if (artifact.kind === "video" && statusLabel(artifact) === "failed") {
            return (
              <Paper key={artifact.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={1}>{artifact.name}</Typography>
                <video src={url} controls className="w-full max-h-64" />
              </Paper>
            );
          }
          if (artifact.kind === "trace" || artifact.kind === "log") {
            return (
              <Paper
                key={artifact.id}
                variant="outlined"
                sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
              >
                <div>
                  <Typography variant="body2" fontWeight={600}>{artifact.name}</Typography>
                  <Chip size="small" label={artifact.kind} />
                </div>
                <Button onClick={() => downloadArtifact(artifact)} disabled={!runId}>
                  Download
                </Button>
              </Paper>
            );
          }
          return null;
        })}
      </div>
      {artifacts.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
          No artifacts match these filters.
        </Typography>
      )}
      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="xl">
        <DialogTitle>{lightbox?.name}</DialogTitle>
        <DialogContent>
          {lightbox && (
            <img
              src={previewUrl(lightbox)}
              alt={lightbox.name}
              className="max-h-[80vh] max-w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtifactGallery;
